<script setup>
import {
  computed,
  ref
} from 'vue'
import { useStore } from 'vuex'
import {
  useRoute,
} from 'vue-router'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { dayOfWeek } from '../../../../modules/dayOfWeekConst'

import {
  KkStatusMessage,
  KkSnsServiceIconList,
  KkCategoryTip,
  KkActionButtonList,
  KkSnsPostContent,
  KkAccountsModal,
  KkActionModal
} from '../../../../components/domains/sns'

const store = useStore()
const { t } = useI18n()

const props = defineProps({
  id: {
    type: Number,
    required: true
  },
  status: {
    type: String,
    default: '',
  },
  lastEditedAt: {
    type: String,
    default: '',
  },
  applicantName: {
    type: String,
    default: ''
  },
  editor: {
    type: String,
    default: ''
  },
  category: {
    type: Object,
    default: () => {},
  },
  title: {
    type: String,
    default: '',
  },
  images: {
    type: Array,
    default: () => []
  },
  movie: {
    type: Object,
    default: () => {}
  },
  publishedAt: {
    type: Date,
    require: false
  },
  scheduledAt: {
    type: Date,
    require: false
  },
  postApprovalFlow: {
    type: Boolean,
    default: false
  },
  approverName: {
    type: String,
    default: ''
  },
  targets: {
    type: Array,
    default: () => []
  },
  authorName: {
    type: String,
    require: true
  }
})
const emit = defineEmits(['delete-approval-post', 'post-approval-post', 'delete-approval-request'])

const showMoreThanOperatorAuthority = computed(() => store.getters['companiesAccessibleApi/showMoreThanOperatorAuthority'])
const showOnlyEditorOrOperatorAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyEditorOrOperatorAuthority'])
const showOnlyOwnerAuthority = computed(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const approvalRequestNotes = computed(() => store.getters['companiesStoreSnsPosts/approvalRequestNotes'])
const showBasicInfoEditModal = ref(false)
const showSnsAccountModal = ref(false)
const showApproveModal = ref(false)
const showPreviewModal = ref(false)
const { company_uuid } = useRoute().params
const getDraftedPost = (payload) => store.dispatch('companiesStoreSnsPosts/getDraftedPost', payload)
const getRequestingPost = (payload) => store.dispatch('companiesStoreSnsPosts/getRequestingPost', payload)
const updateApprovalRequest = ({ id, isApprove, payload }) => store.dispatch('companiesStoreSnsPosts/updateApprovalRequest', { id, isApprove, payload })
const updateSubmitActionModalType = (action) => store.dispatch('companiesStoreSnsPosts/updateSubmitActionModalType', action)

const publishedDateTime = computed(() => {
  if (!props.publishedAt && !props.scheduledAt) return '-'

  const dateTime = props.publishedAt ? props.publishedAt : props.scheduledAt
  const dayOfWeek = dayOfWeekOfReservationDate.value.charAt(0)
  return moment(dateTime).format(`${t('format.date.default')}(${dayOfWeek}) ${t('format.time.default')}`)
})
const dayOfWeekOfReservationDate = computed(() => {
  const dowNumber = moment(props.scheduledAt).day()
  return Object.values(dayOfWeek)[dowNumber]
})

const lastEditedDateTime = computed(() => moment(props.lastEditedAt).format(t('format.datetime.default')))
const approvalLatestStatusMessage = computed(() => {
  if(props.status === 'rejected' && !props.editor) return t('models.approvalLatestStatus.rejectedPassedSchedule')

  return t('models.approvalLatestStatus.' + props.status, { editor: props.editor })
})

const approver = computed(() => {
  if(props.postApprovalFlow && props.approverName) {
    return `(${t('pages.CompaniesStoreSnsPost.index.approverName', { approver: props.approverName })})`
  }
  return null
})

const applicant = computed(() => {
  if(props.postApprovalFlow && props.applicantName) {
    return `(${t('pages.CompaniesStoreSnsPost.index.applicantName', { applicant: props.applicantName })})`
  }
  return null
})

const openApproveModal = ()  => {
  updateSubmitActionModalType('approve')
  showApproveModal.value = true
  const payload = {
    id: props.id,
    isPreview: true,
  }
  getRequestingPost(payload)
}

const openPreviewModal = () => {
  const payload = {
    id: props.id,
    isPreview: true,
  }
  showPreviewModal.value = true
  props.status === 'draft' ? getDraftedPost(payload) : getRequestingPost(payload)
}
const postApprovalSubmit = async (isApprove) => {
  await updateApprovalRequest({
    id: props.id,
    isApprove: isApprove,
    payload: { comment: approvalRequestNotes.value }
  })

  emit('post-approval-post')
}

const deletePost = async (id) => {
  emit('delete-approval-post', { id: id, status: props.status} )
}

const deleteApprovalRequest = async (id) => {
  emit('delete-approval-request', { id: id, status: props.status} )
}

const canEdit = computed(()=> {
  if(!showMoreThanOperatorAuthority.value) return false

  if (['draft', 'rejected'].includes(props.status)) return true
  return showOnlyOwnerAuthority.value
})

const canDelete = computed(() => {
  if(showOnlyOwnerAuthority.value) return true
  if(!showMoreThanOperatorAuthority.value) return false

  if (['draft', 'rejected'].includes(props.status)) return true

  const isAfter = scheduledMoment.value.isAfter(moment())
  const isRequesting = props.status === 'requesting'
  // 投稿予約時間までは編集OK && 承認待ちの状態で編集できない
  return isAfter && !isRequesting
})

const canApprove = computed(()=> showOnlyOwnerAuthority.value && props.status === 'requesting')

const scheduledMoment = computed(() => moment(props.scheduledAt))

const canCancelRequestApproving = computed(() => {
  const isAfter = scheduledMoment.value.isAfter(moment())
  return isAfter && showOnlyEditorOrOperatorAuthority.value && props.status === 'requesting'
})
const routeParameterEdit = computed(() => {
  if(props.status === 'draft') {
    return { name: 'CompaniesStoreSnsDraftedPostsEdit', params: { company_uuid: company_uuid, drafted_post_id: props.id } }
  }

  if(props.status === 'rejected' || props.status === 'requesting') {
    return { name: 'CompaniesStoreSnsRequestingPostsEdit', params: { company_uuid: company_uuid, requesting_post_id: props.id } }
  }

  return { name: 'CompaniesStoreSnsPostsEdit', params: { company_uuid: company_uuid, post_id: props.id } }

})

const routeParameterCopy = computed(() => {
  if (props.status === 'draft') {
    return { name: 'CompaniesStoreSnsPostsNew', params: { company_uuid: company_uuid }, query: { drafted_post_copy: props.id } }
  }

  if (props.status === 'rejected' || props.status === 'requesting') {
    return { name: 'CompaniesStoreSnsPostsNew', params: { company_uuid: company_uuid }, query: { requesting_post_copy: props.id } }
  }

  return { name: 'CompaniesStoreSnsPostsNew', params: { company_uuid: company_uuid }, query: { copy: props.id } }
})

const postedSns = () => {
  const targetSns = props.targets.map(v => v.kind)
  return [...new Set(targetSns)]
}
</script>

<template>
  <tr class="card">
    <td class="card__status">
      <KkStatusMessage v-if="props.status" :status="props.status" />
      <p>{{ lastEditedDateTime }}</p>
      <p>{{ approvalLatestStatusMessage }}</p>
      <div v-if="postApprovalFlow && approverName" class="stakeholder">
        {{ approver }}
      </div>
      <div v-if="postApprovalFlow && applicantName" class="stakeholder">
        {{ applicant }}
      </div>
      <div class="stakeholder">
        ({{ t('pages.CompaniesStoreSnsPost.index.authorName', { author: props.authorName }) }})
      </div>
    </td>
    <td class="card__title">
      <KkCategoryTip :name="props.category.name" :is-editable="false" @click="showBasicInfoEditModal = !showBasicInfoEditModal" />
      <p v-if="title" class="title">{{ title }}</p>
    </td>
    <td class="card__content">
      <KkSnsPostContent :images="props.images" :movie="props.movie" height="80px" />
    </td>
    <td class="card__setting">
      <div class="published">
        {{ t('pages.CompaniesStoreSnsPost.index.bookingDate') }}：<time class="published__at">{{ publishedDateTime }}</time>
      </div>
      <KkSnsServiceIconList :posted-sns="postedSns()" />
      <button
        type="button"
        class="account"
        @click="showSnsAccountModal = !showSnsAccountModal"
      >
        {{ props.targets.length }}アカウント
      </button>
      <KkAccountsModal
        v-if="showSnsAccountModal"
        :targets="props.targets"
        :published-at="props.publishedAt"
        :scheduled-at="props.scheduledAt"
        :status="props.status"
        :open-modal="showSnsAccountModal"
        @close="showSnsAccountModal = false"
      />
      <KkActionModal
        v-if="showPreviewModal"
        :is-preview="true"
        :show-action-modal="showPreviewModal"
        @close-action-modal="showPreviewModal = false"
      />
      <KkActionModal
        v-if="showApproveModal"
        :is-preview="false"
        :show-action-modal="showApproveModal"
        @post-approval-submit="postApprovalSubmit"
        @close-action-modal="showApproveModal = false"
      />
    </td>
    <td class="card__option">
      <KkActionButtonList
        :show-edit="canEdit"
        :show-approve-detail="canApprove"
        :show-preview="!canApprove"
        :show-copy="canEdit"
        :route-parameter-edit="routeParameterEdit"
        :route-parameter-copy="routeParameterCopy"
        :show-delete="canDelete"
        :show-cancel-request="canCancelRequestApproving"
        @clickShowApproveButton="openApproveModal"
        @clickPreviewButton="openPreviewModal"
        @clickDeleteButton="deletePost(props.id)"
        @clickCancelRequestButton="deleteApprovalRequest(props.id)"
      />
    </td>
  </tr>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.card {
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }

  &__status {
    font-size: 12px;
    border-radius: 4px;
    p:not(:first-child) {
      margin-top: 8px;
    }

    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }

    .stakeholder {
      font-size: 12px;
    }
  }

  &__title {
    width: 25%;
    margin-right: 40px;

    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }

    .title {
      margin-bottom: 0;
      font-size: 14px;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  &__content {
    flex: none;
    margin-right: 24px;
    width: 15%;

    @media screen and (max-width: 768px) {
      width: 100%;
      margin-right: 0;
    }
  }

  &__setting {
    width: 25%;
    margin-right: 40px;

    @media screen and (max-width: 768px) {
      width: 100%;
      margin: 16px 0 0;
    }
    align-items: center;
    .published {
      display: flex;
      align-items: center;
      font-size: 12px;
      line-height: 1.5;
      margin-bottom: 16px;
    }

    .account {
      margin-top: 10px;
      font-size: 12px;
      line-height: 1.5;
      align-items: center;
      text-decoration: underline
    }
  }

  &__option {
    border-right: 1px solid #E4E7EB;
    border-radius: 4px;
  }
  &__action {
    flex-wrap: wrap;
    align-items: center;
    margin: 0 -8px -8px;
  }
}

td {
  vertical-align: top;
  border-bottom: 1px solid #E4E7EB;
}

td:not(:first-child) {
  border-left: none;
}
</style>
